/** Categorias: a lista que o resto do app já consumia e ninguém podia editar (lacuna L15).
 *
 * O bucket não é enfeite: é ele que diz ao motor em que coluna o lançamento entra e o que sai
 * do diário mensal. Por isso a tela é organizada por bucket, e não por ordem alfabética — a
 * pergunta na hora de criar é "isto é fixo, variável, do dia a dia ou reserva?".
 *
 * A folha de ação (criar, renomear, excluir) repete o padrão do Planejado: uma categoria errada
 * suja todo lançamento que a usa, então desfazer precisa estar ao lado de fazer.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Empty, ErrorBanner, Face, Field, FieldGroup, Key, Label, Skeleton } from "../components/ui";
import { Icon } from "../components/Icon";
import {
  keys,
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../lib/queries";
import * as api from "../lib/api";
import type { Bucket, Category } from "../lib/types";

/** A tecla do sistema mede 32px; no dedo o alvo mínimo é 44. */
const ALVO = { minHeight: 44 };

const BUCKETS: { id: Bucket; label: string; pena: "in" | "out" | "dy" | "sv"; sub: string }[] = [
  { id: "income", label: "Entradas", pena: "in", sub: "o dinheiro que chega" },
  { id: "fixed", label: "Fixas", pena: "out", sub: "o valor não muda de mês para mês" },
  { id: "variable", label: "Variáveis", pena: "out", sub: "existem todo mês, o valor muda" },
  { id: "daily", label: "Diário", pena: "dy", sub: "o gasto do dia a dia, que sai do diário mensal" },
  { id: "savings", label: "Economia", pena: "sv", sub: "sai do fluxo e fica guardado" },
];

type Alvo = { bucket: Bucket; cat?: Category };

export function Categorias() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const cats = useCategories();
  // `seedCategories` não tem hook em queries.ts (arquivo fechado nesta tarefa).
  const padrao = useMutation({
    mutationFn: api.seedCategories,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories }),
  });
  const [alvo, setAlvo] = useState<Alvo | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const cabeca = (
    <div className="monthbar">
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <Key icon="chevronLeft" title="Voltar" onClick={() => nav("/ajustes")} style={ALVO} />
        <h2>Categorias</h2>
      </div>
    </div>
  );

  if (cats.isPending)
    return (
      <section className="view on" aria-label="Categorias">
        <Skeleton lines={6} />
      </section>
    );

  if (cats.error)
    return (
      <section className="view on" aria-label="Categorias">
        <div className="plan">
          {cabeca}
          <ErrorBanner message={(cats.error as Error).message} onRetry={() => cats.refetch()} />
        </div>
      </section>
    );

  const lista = cats.data ?? [];

  const restaurar = async () => {
    setErro(null);
    try {
      await padrao.mutateAsync();
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  return (
    <section className="view on" aria-label="Categorias">
      <div className="plan">
        {cabeca}

        <Face className="meter">
          <Label>Para que servem</Label>
          <div className="sub">
            A categoria diz em que coluna o lançamento entra e o que o motor desconta do diário
            mensal. Você pode ter as suas; as padrão ficam de rede de segurança.
          </div>
          <div style={{ marginTop: 8 }}>
            <Key style={ALVO} disabled={padrao.isPending} onClick={restaurar}>
              {padrao.isPending ? "Restaurando…" : "Restaurar as padrão"}
            </Key>
          </div>
          <div className="rule">
            Restaurar só acrescenta o que falta: nenhuma categoria sua é apagada ou renomeada.
          </div>
          {erro && <ErrorBanner message={erro} />}
        </Face>

        {BUCKETS.map((b) => {
          const doBucket = lista.filter((c) => c.bucket === b.id);
          return (
            <Face className="log" key={b.id}>
              <div className="sec">
                <Label>{b.label}</Label>
                <Label dim>{b.sub}</Label>
              </div>
              {doBucket.length === 0 && <Empty title="Nenhuma aqui" />}
              {doBucket.map((c) => (
                <div className={`line p-${b.pena}`} key={c.id}>
                  <div className="d" />
                  <div className="what">
                    <div className="t">
                      <span className="desc">{c.name}</span>
                    </div>
                  </div>
                  <div style={{ paddingRight: 14 }}>
                    <Key
                      icon="pen"
                      style={ALVO}
                      title={`Renomear ou excluir ${c.name}`}
                      onClick={() => setAlvo({ bucket: b.id, cat: c })}
                    />
                  </div>
                </div>
              ))}
              <button type="button" className="add" style={ALVO} onClick={() => setAlvo({ bucket: b.id })}>
                <Icon name="plus" />
                Nova em {b.label.toLowerCase()}
              </button>
            </Face>
          );
        })}
      </div>

      {alvo && <FolhaCategoria alvo={alvo} todas={lista} onClose={() => setAlvo(null)} />}
    </section>
  );
}

function FolhaCategoria({
  alvo,
  todas,
  onClose,
}: {
  alvo: Alvo;
  todas: Category[];
  onClose: () => void;
}) {
  const criar = useCreateCategory();
  const renomear = useUpdateCategory();
  const apagar = useDeleteCategory();

  const [nome, setNome] = useState(alvo.cat?.name ?? "");
  const [bucket, setBucket] = useState<Bucket>(alvo.cat?.bucket ?? alvo.bucket);
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const ocupado = criar.isPending || renomear.isPending || apagar.isPending;
  const limpo = nome.trim();
  // O banco tem UNIQUE (user_id, name) e responde 500 sem mensagem útil: o choque de nome
  // é barrado aqui, antes de sair da tela.
  const repetido = todas.some(
    (c) => c.id !== alvo.cat?.id && c.name.toLowerCase() === limpo.toLowerCase(),
  );
  const podeSalvar = limpo.length > 0 && !repetido && !ocupado;

  const salvar = async () => {
    setErro(null);
    try {
      if (alvo.cat) await renomear.mutateAsync({ id: alvo.cat.id, name: limpo, bucket });
      else await criar.mutateAsync({ name: limpo, bucket });
      onClose();
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  const excluir = async () => {
    setErro(null);
    try {
      await apagar.mutateAsync(alvo.cat!.id);
      onClose();
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-labelledby="cat-titulo">
      <div className="panel">
        <div className="handle" />
        <h2 id="cat-titulo">
          <span>{alvo.cat ? alvo.cat.name : "Nova categoria"}</span>
          <Key icon="x" title="Fechar" onClick={onClose} style={ALVO} />
        </h2>

        <Field label="Nome">
          <input
            value={nome}
            autoFocus
            placeholder="Ex.: Mercado, Escola da Eulália"
            onChange={(e) => setNome(e.target.value)}
          />
        </Field>
        {repetido && <div className="rule">Já existe uma categoria com esse nome.</div>}

        <FieldGroup label="Onde entra">
          <div className="chips">
            {BUCKETS.map((b) => (
              <button
                key={b.id}
                type="button"
                className="chip"
                style={ALVO}
                aria-pressed={bucket === b.id}
                onClick={() => setBucket(b.id)}
              >
                {b.label}
              </button>
            ))}
          </div>
        </FieldGroup>
        <div className="rule">{BUCKETS.find((b) => b.id === bucket)?.sub}</div>

        {erro && (
          <div className="banner" role="alert">
            {erro}
          </div>
        )}

        <div className="acts">
          <Key onClick={onClose} style={ALVO}>
            Cancelar
          </Key>
          <Key primary disabled={!podeSalvar} onClick={salvar} style={ALVO}>
            {ocupado ? "Salvando…" : alvo.cat ? "Salvar" : "Criar"}
          </Key>
        </div>

        {alvo.cat && (
          <>
            <div className="rule" style={{ marginTop: 10 }}>
              Excluir não apaga lançamento nenhum: os que usavam esta categoria ficam sem
              categoria, e saem das somas por categoria.
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              {!confirmando ? (
                <Key danger style={ALVO} disabled={ocupado} onClick={() => setConfirmando(true)}>
                  Excluir
                </Key>
              ) : (
                <>
                  <Key style={ALVO} onClick={() => setConfirmando(false)}>
                    Não
                  </Key>
                  <Key danger style={ALVO} disabled={ocupado} onClick={excluir}>
                    {apagar.isPending ? "Excluindo…" : "Confirmar exclusão"}
                  </Key>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
